import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ConsolePage,
  ConsoleStatStrip,
  ConsoleTable,
  consoleTokens,
  FilterToken,
  Kbd,
} from '@components/ui/console';
import { useAppointmentLabelsEnabled, useGetLabelsQuery } from '@features/appointment-labels';
import { useOrganizationId } from '@features/user';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useReportPageShortcuts } from '../hooks/useReportPageShortcuts';
import { useReportRun } from '../hooks/useReportRun';
import { useReportsAccess } from '../hooks/useReportsAccess';
import { useReportDefinitions } from '../store/hooks';
import type { ReportColumn, ReportFilterSpec } from '../types';
import {
  asText,
  buildBandMeta,
  buildConsoleColumns,
  buildConsoleStats,
} from '../utils/consoleAdapter';
import { toIsoDate } from '../utils/format';

import { ColumnsPopover } from './ColumnsPopover';
import { ExportMenu } from './ExportMenu';
import { describeFilter, FilterPopover } from './FilterPopover';
import { PeriodPopover } from './PeriodPopover';
import { SaveViewDialog, type SaveViewSummaryRow } from './SaveViewDialog';

// User-facing basis names say what a row IS, not where its date came from.
const BASIS_SHORT_LABELS: Record<string, string> = {
  service_date: 'sessions',
  payment_posted: 'payments',
};

const BASIS_MENU_COPY: Record<string, { title: string; description: string }> = {
  service_date: {
    title: 'Sessions delivered',
    description:
      'One row per session with a date of service in the period. Collections show what has been received for those sessions to date.',
  },
  payment_posted: {
    title: 'Payments posted',
    description:
      'One row per payment posted in the period — including late payments for sessions from earlier periods.',
  },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatPeriodLabel = (start: string, end: string): string => {
  const [startYear, startMonth, startDay] = start.split('-').map(Number);
  const [endYear, endMonth, endDay] = end.split('-').map(Number);
  const startLabel = `${MONTHS[startMonth - 1]} ${startDay}`;
  if (startYear !== endYear)
    return `${startLabel} ${startYear} – ${MONTHS[endMonth - 1]} ${endDay} ${endYear}`;
  if (startMonth !== endMonth) return `${startLabel} – ${MONTHS[endMonth - 1]} ${endDay}`;
  return `${startLabel}–${endDay}`;
};

export interface ReportConsoleProps {
  reportKey: string;
  /** Drill-in mode: no URL/localStorage ownership, no page chrome. */
  embedded?: boolean;
  /** Locked, hidden filters the run always applies (a metric's drill-in scope). */
  baseFilters?: ReportFilterSpec[];
  /** Grouping to open with when the user hasn't chosen (drill-in: clinician). */
  initialGroupBy?: string[];
  /** Preselected, editable opening date window (e.g. the calendar's current view). */
  initialWindow?: { start: string; end: string };
  /** Preselected, editable row filters (e.g. the calendar's provider filter). */
  initialFilters?: ReportFilterSpec[];
  /** Back affordance; rendered only when provided (standalone page). */
  onBack?: () => void;
  /** Show the report quick-switcher in the title (standalone page). */
  enableSwitcher?: boolean;
}

/**
 * The console: opens already running with defaults, every filter is a
 * command-bar token, the table is the grouped tree, stats carry deltas. The
 * standalone page wraps this with a back button + report switcher; embedded
 * drill-ins render it chrome-free, pre-scoped to one metric via baseFilters.
 */
export const ReportConsole: React.FC<ReportConsoleProps> = ({
  reportKey,
  embedded = false,
  baseFilters,
  initialGroupBy,
  initialWindow,
  initialFilters,
  onBack,
  enableSwitcher = false,
}) => {
  const navigate = useNavigate();
  const { hasAccess: canSeeReports, isLoading: accessLoading } = useReportsAccess();
  const { reports } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherAnchor = useRef<HTMLButtonElement>(null);
  const { data: definitionsData } = useReportDefinitions();

  const periodAnchor = useRef<HTMLButtonElement>(null);
  const basisAnchor = useRef<HTMLButtonElement>(null);
  const groupAnchor = useRef<HTMLButtonElement>(null);
  const columnsAnchor = useRef<HTMLButtonElement>(null);
  const filterAnchor = useRef<HTMLButtonElement>(null);
  const caveatsAnchor = useRef<HTMLButtonElement>(null);
  const [openToken, setOpenToken] = useState<
    'period' | 'basis' | 'group' | 'columns' | 'filter' | 'caveats' | null
  >(null);

  const {
    definition,
    definitionsLoading,
    window: dateWindow,
    setWindow,
    basis,
    setBasis,
    groupBy,
    setGroupBy,
    columns: pickedColumns,
    setColumns,
    isDefaultColumns,
    sort,
    setSort,
    filters,
    effectiveFilters,
    setFilters,
    isDirty,
    runResult,
    isRunning,
    runError,
    downloadCsv,
    downloadCsvProfile,
    downloadXlsx,
    xlsxPrefs,
    downloadPdf,
    pdfPrefs,
    isExporting,
    saveView,
    isSavingView,
    windowDays,
    appliedSavedViewName,
  } = useReportRun({
    reportKey,
    embedded,
    baseFilters,
    initialGroupBy,
    initialWindow,
    initialFilters,
  });

  const usesDateWindow = definition?.uses_date_window !== false;

  useEffect(() => {
    if (reportKey) {
      reports.reportOpened({ report_key: reportKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportKey]);

  // Group-by choices: none, each dimension, and first-dim→other two-level
  // combos — all derived from the definition.
  const groupOptions = useMemo(() => {
    if (!definition) return [];
    const dims = definition.dimensions;
    const options: { value: string[]; label: string }[] = [
      { value: [], label: 'none' },
      ...dims.map(dimension => ({
        value: [dimension.key],
        label: dimension.label.toLowerCase(),
      })),
    ];
    if (dims.length > 1) {
      options.push(
        ...dims.slice(1).map(dimension => ({
          value: [dims[0].key, dimension.key],
          label: `${dims[0].label.toLowerCase()}→${dimension.label.toLowerCase()}`,
        })),
      );
    }
    return options;
  }, [definition]);

  const groupLabel =
    groupOptions.find(option => option.value.join(',') === groupBy.join(','))?.label ??
    (groupBy.join('→') || 'none');

  const cycleGroupBy = () => {
    if (groupOptions.length === 0) return;
    const index = groupOptions.findIndex(option => option.value.join(',') === groupBy.join(','));
    const next = groupOptions[(index + 1) % groupOptions.length];
    reports.tokenChanged({ report_key: reportKey, token: 'group', via: 'keyboard' });
    setGroupBy(next.value);
  };

  useReportPageShortcuts({
    onCycleGroup: cycleGroupBy,
    onExport: () => {
      void downloadCsv();
    },
    enabled: !embedded && Boolean(definition) && canSeeReports,
  });

  const columns = useMemo(
    () =>
      runResult && definition
        ? buildConsoleColumns(runResult.columns, definition.measures, runResult.group_by)
        : [],
    [runResult, definition],
  );

  // The labels column rides the same APPOINTMENT_LABELS rollout gate as the rest
  // of the feature: drop it (display picker + filter + exports) for orgs that
  // don't have labels enabled, so it never appears where labels are off.
  const labelsEnabled = useAppointmentLabelsEnabled();
  const detailColumns = useMemo(
    () =>
      (definition?.detail_columns ?? []).filter(column => labelsEnabled || column.key !== 'labels'),
    [definition, labelsEnabled],
  );

  const filterableColumns = useMemo(
    () => detailColumns.filter(column => column.filterable),
    [detailColumns],
  );

  // Resolve label ids -> names so the saved-view preview reads "Labels is any
  // of Room 1, Spravato" instead of opaque uuids. Cached + shared with the
  // label filter control; only fetched when labels are enabled.
  const organizationId = useOrganizationId();
  const { data: labelsData } = useGetLabelsQuery(
    { orgId: organizationId ?? '' },
    { skip: !labelsEnabled || !organizationId },
  );
  const labelNamesById = useMemo(
    () => Object.fromEntries((labelsData?.items ?? []).map(label => [label.label_id, label.name])),
    [labelsData],
  );
  // Value suggestions come from the loaded result's own rows (flattened from
  // the group tree) — exactly the values present in this window, no extra
  // endpoint. Columns hidden from the current view won't suggest (rows are
  // key-filtered server-side); free-text entry covers those.
  const suggestionsFor = useMemo(() => {
    const rows = (runResult?.groups ?? []).flatMap(group =>
      group.subgroups ? group.subgroups.flatMap(subgroup => subgroup.rows) : group.rows,
    );
    return (field: string): string[] => {
      const seen = new Set<string>();
      for (const row of rows) {
        const value = (row as Record<string, unknown>)[field];
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          if (value !== '') seen.add(String(value));
        }
      }
      return Array.from(seen).sort();
    };
  }, [runResult]);
  // Deltas compare against the same-length window immediately preceding —
  // surfaced as a hover tooltip so the ▲/▼ is never a mystery number.
  const deltaTooltip = useMemo(() => {
    const start = new Date(`${dateWindow.start}T00:00:00`);
    const end = new Date(`${dateWindow.end}T00:00:00`);
    const lengthDays = Math.round((end.getTime() - start.getTime()) / 86400000);
    const priorEnd = new Date(start);
    priorEnd.setDate(start.getDate() - 1);
    const priorStart = new Date(priorEnd);
    priorStart.setDate(priorEnd.getDate() - lengthDays);
    const priorLabel = formatPeriodLabel(toIsoDate(priorStart), toIsoDate(priorEnd));
    return `vs ${priorLabel} — the preceding period of the same length`;
  }, [dateWindow]);

  const stats = useMemo(
    () => (runResult ? buildConsoleStats(runResult.stats, deltaTooltip) : []),
    [runResult, deltaTooltip],
  );
  // The exact config the save dialog previews (window is handled there).
  const saveSummary: SaveViewSummaryRow[] = useMemo(() => {
    if (!definition) return [];
    const basisLabel =
      BASIS_MENU_COPY[basis ?? '']?.title ?? BASIS_SHORT_LABELS[basis ?? ''] ?? basis ?? '';
    const rows: SaveViewSummaryRow[] = [
      { label: 'Basis', value: basisLabel },
      { label: 'Grouping', value: groupLabel === 'none' ? 'none' : `by ${groupLabel}` },
      {
        label: 'Columns',
        value: isDefaultColumns
          ? `default (${pickedColumns.length})`
          : `${pickedColumns.length} selected`,
      },
    ];
    if (effectiveFilters.length === 0) {
      rows.push({ label: 'Filters', value: 'none' });
    } else {
      rows.push({
        label: 'Filters',
        value: effectiveFilters
          .map(filter => describeFilter(filter, detailColumns, labelNamesById))
          .join('  ·  '),
        mono: true,
      });
    }
    return rows;
  }, [
    definition,
    detailColumns,
    labelNamesById,
    basis,
    groupLabel,
    isDefaultColumns,
    pickedColumns,
    effectiveFilters,
  ]);

  const bandMeta = useMemo(
    () => (definition ? buildBandMeta(definition.stat_cards) : undefined),
    [definition],
  );

  // The Σ noun comes from the count-measure's stat card (sessions,
  // appointments…), falling back to "rows".
  const rowNoun = useMemo(() => {
    if (!definition) return 'rows';
    const countMeasure = definition.measures.find(measure => measure.agg === 'count');
    const card = definition.stat_cards.find(statCard => statCard.measure_key === countMeasure?.key);
    return card?.label ?? 'rows';
  }, [definition]);

  if (!accessLoading && !canSeeReports) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning">
          You don&apos;t have permission to view reports. Ask a practice administrator to grant
          report access for your role.
        </Alert>
      </Container>
    );
  }

  if (definitionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!definition) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">Unknown report: {reportKey}</Alert>
      </Container>
    );
  }

  const handleSaveView = async (name: string, windowKind: 'relative_days' | 'absolute') => {
    try {
      await saveView(name, windowKind);
      setSaveDialogOpen(false);
      enqueueSnackbar('Report view saved', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not save the report view', { variant: 'error' });
    }
  };

  const caveats = runResult?.caveats ?? definition.caveats;
  const groupedDimensionLabel = definition.dimensions.find(
    dimension => dimension.key === (runResult?.group_by[0] ?? groupBy[0]),
  )?.label;
  // A row's meaning is basis-dependent (sessions vs per-line payments), so the
  // Σ footer noun follows the active basis, falling back to the stat-card noun.
  const totalsNoun = (runResult && BASIS_SHORT_LABELS[runResult.basis]) || rowNoun;
  const totalsLabel = runResult
    ? `Σ ${runResult.totals.row_count} ${totalsNoun}` +
      (runResult.group_by.length > 0 && groupedDimensionLabel
        ? ` · ${runResult.groups.length} ${groupedDimensionLabel.toLowerCase()}${
            runResult.groups.length === 1 ? '' : 's'
          }`
        : '')
    : undefined;

  return (
    <>
      <ConsolePage
        title={
          <>
            {onBack && (
              <IconButton
                size="small"
                aria-label="Back to reports"
                onClick={onBack}
                sx={{ color: consoleTokens.dim, mr: 0.5 }}
              >
                <ArrowBackIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            {enableSwitcher ? (
              <Box
                component="button"
                type="button"
                ref={switcherAnchor}
                onClick={() => setSwitcherOpen(true)}
                aria-label="Switch report"
                sx={{
                  alignItems: 'center',
                  bgcolor: 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  color: consoleTokens.ink,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  font: 'inherit',
                  fontWeight: 600,
                  px: 0.5,
                  py: 0.25,
                  '&:hover': { bgcolor: consoleTokens.tokenBg },
                }}
              >
                {definition.title}
                <ArrowDropDownIcon sx={{ color: consoleTokens.fainter, fontSize: 18 }} />
              </Box>
            ) : (
              <Box component="span" sx={{ color: consoleTokens.ink, fontWeight: 600 }}>
                {definition.title}
              </Box>
            )}
            {appliedSavedViewName && (
              <Box
                component="span"
                sx={{
                  color: consoleTokens.dim,
                  fontFamily: consoleTokens.mono,
                  fontSize: '0.6875rem',
                  fontWeight: 400,
                  ml: 0.5,
                }}
              >
                · {appliedSavedViewName}
              </Box>
            )}
          </>
        }
        tokens={
          <>
            {usesDateWindow && (
              <FilterToken
                ref={periodAnchor}
                tokenKey="period"
                value={formatPeriodLabel(dateWindow.start, dateWindow.end)}
                onClick={() => setOpenToken('period')}
              />
            )}
            {definition.supported_bases.length > 1 && (
              <FilterToken
                ref={basisAnchor}
                tokenKey="basis"
                value={BASIS_SHORT_LABELS[basis ?? ''] ?? basis ?? ''}
                onClick={() => setOpenToken('basis')}
              />
            )}
            {definition.dimensions.length > 0 && (
              <FilterToken
                ref={groupAnchor}
                tokenKey="group"
                value={groupLabel}
                onClick={() => setOpenToken('group')}
              />
            )}
            <FilterToken
              ref={columnsAnchor}
              tokenKey="columns"
              value={isDefaultColumns ? 'default' : String(pickedColumns.length)}
              onClick={() => setOpenToken('columns')}
            />
            <FilterToken
              ref={filterAnchor}
              tokenKey="filter"
              value={effectiveFilters.length === 0 ? 'none' : String(effectiveFilters.length)}
              onClick={() => setOpenToken('filter')}
            />
            {caveats.length > 0 && (
              <IconButton
                ref={caveatsAnchor}
                size="small"
                aria-label="Data caveats"
                onClick={() => setOpenToken('caveats')}
                sx={{ color: consoleTokens.fainter }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </>
        }
        commandBarRight={
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            {!embedded && (
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  alignItems: 'center',
                  color: consoleTokens.dim,
                  display: { xs: 'none', md: 'flex' },
                  fontSize: '0.75rem',
                }}
              >
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                  <Kbd>⌘K</Kbd>
                  <span>switch</span>
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                  <Kbd>G</Kbd>
                  <span>group</span>
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                  <Kbd>E</Kbd>
                  <span>export</span>
                </Stack>
              </Stack>
            )}
            {isDirty ? (
              <Button
                size="small"
                startIcon={<BookmarkAddIcon sx={{ fontSize: 16 }} />}
                onClick={() => setSaveDialogOpen(true)}
                sx={{
                  bgcolor: consoleTokens.tokenBg,
                  border: `1px solid ${consoleTokens.amber}`,
                  color: consoleTokens.ink,
                  flexShrink: 0,
                  fontSize: '0.75rem',
                  px: 1,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: consoleTokens.tokenHoverBg },
                }}
              >
                Save view
                <Box
                  sx={{
                    bgcolor: consoleTokens.amber,
                    borderRadius: '50%',
                    height: 6,
                    ml: 0.75,
                    width: 6,
                  }}
                />
              </Button>
            ) : (
              <Tooltip title="Save this configuration as a named view">
                <IconButton
                  size="small"
                  aria-label="Save view"
                  onClick={() => setSaveDialogOpen(true)}
                  sx={{ color: consoleTokens.dim }}
                >
                  <BookmarkAddIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <ExportMenu
              dimensions={definition.dimensions.filter(dimension => dimension.splittable)}
              viewColumns={pickedColumns
                .map(key => detailColumns.find(column => column.key === key))
                .filter((column): column is ReportColumn => Boolean(column))}
              allColumns={detailColumns}
              currentGroupLabels={(runResult?.groups ?? []).map(
                group => group.label ?? 'Unassigned',
              )}
              currentPrimaryDimension={runResult?.group_by[0] ?? groupBy[0] ?? ''}
              xlsxPrefs={xlsxPrefs}
              pdfPrefs={pdfPrefs}
              csvExportProfiles={definition.csv_export_profiles ?? []}
              disabled={isExporting}
              onCsv={options => void downloadCsv(options)}
              onCsvProfile={profileKey => void downloadCsvProfile(profileKey)}
              onXlsx={options => void downloadXlsx(options)}
              onPdf={options => void downloadPdf(options)}
            />
          </Stack>
        }
        statStrip={
          <ConsoleStatStrip
            stats={
              stats.length > 0
                ? stats
                : definition.stat_cards.map(card => ({
                    key: card.key,
                    label: card.label,
                    value: '',
                  }))
            }
            loading={!runResult}
          />
        }
      >
        {runError ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Report failed to run. Check the parameters and try again.
          </Alert>
        ) : (
          // Bottom padding gives the sticky Σ footer somewhere to land when
          // the user reaches the end of the table.
          <Box sx={{ pb: 6 }}>
            <ConsoleTable
              columns={columns}
              groups={runResult?.groups ?? []}
              totalsAggregates={runResult?.totals.aggregates}
              totalsLabel={totalsLabel}
              bandMeta={bandMeta}
              rowKey={(row, index) =>
                asText(row.encounter_id ?? row.appointment_id ?? row.patient_id) || String(index)
              }
              moreRowsNoun={rowNoun}
              loading={!runResult || (isRunning && columns.length === 0)}
              refreshing={isRunning && runResult != null && columns.length > 0}
              emptyMessage={
                usesDateWindow
                  ? 'No rows in this window. Try widening the period or switching basis.'
                  : 'No rows match the current filters.'
              }
              sortKey={sort?.key ?? null}
              sortDir={sort?.dir ?? 'asc'}
              onSortChange={(key, dir) => {
                setSort(key ? { key, dir } : null);
                reports.tokenChanged({
                  report_key: reportKey,
                  token: 'sort',
                  via: 'pointer',
                });
              }}
              onGroupToggle={(_groupKey, collapsed) =>
                reports.groupToggled({ report_key: reportKey, collapsed })
              }
            />
          </Box>
        )}
      </ConsolePage>

      {/* Report quick-switcher (title dropdown) */}
      {enableSwitcher && (
        <Menu
          open={switcherOpen}
          anchorEl={switcherAnchor.current}
          onClose={() => setSwitcherOpen(false)}
        >
          {(definitionsData?.reports ?? []).map(report => (
            <MenuItem
              key={report.key}
              selected={report.key === reportKey}
              onClick={() => {
                setSwitcherOpen(false);
                if (report.key !== reportKey) navigate(`/reports-analytics/${report.key}`);
              }}
              sx={{ fontSize: '0.8125rem' }}
            >
              {report.title}
            </MenuItem>
          ))}
        </Menu>
      )}

      {/* Token editors */}
      {usesDateWindow && (
        <PeriodPopover
          open={openToken === 'period'}
          anchorEl={periodAnchor.current}
          onClose={() => setOpenToken(null)}
          forward={Boolean(definition.forward_window)}
          window={dateWindow}
          onChange={next => {
            reports.tokenChanged({ report_key: reportKey, token: 'period', via: 'pointer' });
            setWindow(next);
          }}
        />
      )}
      <Menu
        open={openToken === 'basis'}
        anchorEl={basisAnchor.current}
        onClose={() => setOpenToken(null)}
        slotProps={{ paper: { sx: { maxWidth: 340 } } }}
      >
        {definition.supported_bases.map(supportedBasis => {
          const copy = BASIS_MENU_COPY[supportedBasis];
          return (
            <MenuItem
              key={supportedBasis}
              selected={supportedBasis === basis}
              onClick={() => {
                reports.tokenChanged({ report_key: reportKey, token: 'basis', via: 'pointer' });
                setBasis(supportedBasis);
                setOpenToken(null);
              }}
              sx={{ alignItems: 'flex-start', whiteSpace: 'normal' }}
            >
              <Stack spacing={0.25} sx={{ py: 0.5 }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                  {copy?.title ?? supportedBasis}
                </Typography>
                <Typography sx={{ color: consoleTokens.dim, fontSize: '0.6875rem' }}>
                  {copy?.description ?? ''}
                </Typography>
              </Stack>
            </MenuItem>
          );
        })}
        <Typography
          sx={{
            borderTop: `1px solid ${consoleTokens.line}`,
            color: consoleTokens.fainter,
            fontSize: '0.625rem',
            px: 2,
            pt: 1,
          }}
        >
          The basis sets what a row means — totals follow it.
        </Typography>
      </Menu>
      <Menu
        open={openToken === 'group'}
        anchorEl={groupAnchor.current}
        onClose={() => setOpenToken(null)}
      >
        {groupOptions.map(option => (
          <MenuItem
            key={option.value.join(',') || 'none'}
            selected={option.value.join(',') === groupBy.join(',')}
            onClick={() => {
              reports.tokenChanged({ report_key: reportKey, token: 'group', via: 'pointer' });
              setGroupBy(option.value);
              setOpenToken(null);
            }}
          >
            <Typography sx={{ fontSize: '0.8125rem' }}>{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
      <ColumnsPopover
        open={openToken === 'columns'}
        anchorEl={columnsAnchor.current}
        onClose={() => setOpenToken(null)}
        allColumns={detailColumns}
        selected={pickedColumns}
        defaultColumns={definition.default_columns}
        onChange={next => {
          reports.tokenChanged({ report_key: reportKey, token: 'columns', via: 'pointer' });
          setColumns(next);
        }}
      />
      <FilterPopover
        open={openToken === 'filter'}
        anchorEl={filterAnchor.current}
        onClose={() => setOpenToken(null)}
        columns={filterableColumns}
        filters={filters}
        onChange={next => {
          reports.tokenChanged({ report_key: reportKey, token: 'filter', via: 'pointer' });
          setFilters(next);
        }}
        suggestionsFor={suggestionsFor}
        onLabelFilterChange={labelCount =>
          reports.labelFilterChanged({ report_key: reportKey, label_count: labelCount })
        }
      />
      <Popover
        open={openToken === 'caveats'}
        anchorEl={caveatsAnchor.current}
        onClose={() => setOpenToken(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ maxWidth: 420, p: 2 }}>
          <Typography
            sx={{
              color: consoleTokens.dim,
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              mb: 1,
            }}
          >
            ABOUT THESE NUMBERS
          </Typography>
          <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2 }}>
            {caveats.map(caveat => (
              <Typography
                component="li"
                key={caveat}
                sx={{ color: consoleTokens.dim, fontSize: '0.75rem' }}
              >
                {caveat}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Popover>

      <SaveViewDialog
        open={saveDialogOpen}
        reportTitle={definition.title}
        windowDays={windowDays}
        periodLabel={formatPeriodLabel(dateWindow.start, dateWindow.end)}
        usesDateWindow={usesDateWindow}
        summary={saveSummary}
        onClose={() => setSaveDialogOpen(false)}
        onSave={handleSaveView}
        isSaving={isSavingView}
      />
    </>
  );
};

/**
 * Keyed by report so switching reports remounts the console with fresh token
 * state — one report's basis/group/columns must never ride into another's run
 * request. The standalone page wires the back button + report switcher.
 */
export const ReportRunPage: React.FC = () => {
  const { reportKey = '' } = useParams<{ reportKey: string }>();
  const navigate = useNavigate();
  return (
    <ReportConsole
      key={reportKey}
      reportKey={reportKey}
      onBack={() => navigate('/reports-analytics')}
      enableSwitcher
    />
  );
};
